import { canAnimateElement, readCssTimeMs } from '../../components/motion-policy.js';
import { playDetailsHeightMotion } from '../../components/details-motion.js';

/**
 * The archive of yearly proposals. Same disclosure motion as the create form - one screen, one way of
 * opening - and the same JS-owned `aria-expanded`, which the label and the chevron read while `open` is
 * still true through the close.
 */
export function initRankingEarlierYears(
    documentRef = globalThis.document,
    windowRef = globalThis.window,
) {
    const disclosure = documentRef?.querySelector?.('[data-rankings-earlier]');
    if (!(disclosure instanceof HTMLDetailsElement) || disclosure.dataset.rankingsEarlierBound === 'true') {
        return;
    }

    const trigger = disclosure.querySelector('.rankings-earlier-trigger');
    const content = disclosure.querySelector('.rankings-earlier-content');
    if (!(trigger instanceof HTMLElement) || !(content instanceof HTMLElement)) {
        return;
    }

    disclosure.dataset.rankingsEarlierBound = 'true';
    trigger.setAttribute('aria-expanded', disclosure.open ? 'true' : 'false');
    content.inert = !disclosure.open;
    let isAnimating = false;

    const applyExpandedState = (expanded) => {
        disclosure.open = expanded;
        content.inert = !expanded;
        trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    };

    const transitionExpandedState = async (expanded) => {
        if (isAnimating || disclosure.open === expanded) {
            return;
        }

        if (!canAnimateElement(disclosure, windowRef)) {
            applyExpandedState(expanded);
            return;
        }

        isAnimating = true;
        const startHeight = disclosure.getBoundingClientRect().height;
        if (expanded) {
            applyExpandedState(true);
            content.inert = true;
        } else {
            content.inert = true;
            trigger.setAttribute('aria-expanded', 'false');
        }

        const endHeight = expanded
            ? disclosure.getBoundingClientRect().height
            : trigger.getBoundingClientRect().height;
        await playDetailsHeightMotion(disclosure, content, {
            startHeight,
            endHeight,
            expanded,
            duration: readCssTimeMs(
                disclosure,
                expanded ? '--rankings-create-open-duration' : '--rankings-create-close-duration',
                expanded ? 320 : 260,
            ),
        });

        if (!expanded) {
            applyExpandedState(false);
        }
        disclosure.style.removeProperty('height');
        disclosure.style.removeProperty('overflow');
        content.inert = !expanded;
        isAnimating = false;
    };

    disclosure.addEventListener('toggle', () => {
        if (!isAnimating) {
            content.inert = !disclosure.open;
            trigger.setAttribute('aria-expanded', disclosure.open ? 'true' : 'false');
        }
    });

    trigger.addEventListener('click', (event) => {
        event.preventDefault();

        return transitionExpandedState(!disclosure.open);
    });
}
